import { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { Box, Stack, Button, IconButton, Alert, ToggleButton, ToggleButtonGroup, LinearProgress, Skeleton, Chip, Typography, Card, CardContent, Avatar, Tooltip, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Paper, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Popper, ClickAwayListener } from '@mui/material';
import { 
  Refresh as RefreshIcon, 
  People as PeopleIcon, 
  Business as BusinessIcon, 
  Sync as SyncIcon, 
  Warning as WarningIcon, 
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Pause as PauseIcon,
  TrendingUp as TrendingUpIcon,
  Error as ErrorIcon,
  AccessTime as AccessTimeIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Work as WorkIcon,
  CardMembership as CardMembershipIcon,
  OpenInNew as OpenInNewIcon,
  SupportAgent as SupportAgentIcon
} from '@mui/icons-material';

// Components
import { StatCard, StatCardSkeleton, MetricCard, MetricCardSkeleton } from '@/common-components/cards';
import { DataGrid } from '@/common-components/tables';
import { PageHeader, TabNavigation } from '@/common-components/layout';
import ParityBar from '@/common-components/parity-bar/parity-bar';

// Hooks
import { useUrlState } from '@/common-hooks';
import { useDashboardContext } from '@/common-hooks/use-dashboard-context';
import { useAuthentication } from '@/common-hooks/use-authentication';
import { dashboardService } from '@/services/api';
import { PERMISSIONS } from '@/common-constants/permissions';

// Constants
import { TIME_RANGES } from '@/common-constants';
import { getServiceNowIncidentPortalUrlFromTicketRow } from '@/common/utilities/env-utilities';

// Types
import type { 
  HungJob, 
  EarlyFailureJob, 
  LowParityJob, 
  OrganizationDetail, 
  OrgPairAnalyticsItem, 
  UsersByOrganization, 
  Schedule,
  ActiveSubscription,
  ErrorSubscription,
  EndingSoonSubscription,
  OpenTicket
} from '@/common-types';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'jobs', label: 'Jobs' },
  { id: 'organizations', label: 'Organizations' },
  { id: 'users', label: 'Users' },
  { id: 'schedules', label: 'Schedules' },
  { id: 'subscriptions', label: 'Subscriptions' },
  { id: 'tickets', label: 'Tickets' },
];

/** Unassigned tickets open **more than** this many days get red/error row styling and chips. */
const RED_AFTER_DAYS_IF_UNASSIGNED = 2;

function parseIncidentTicketDate(value: string | undefined): number | null {
  if (!value?.trim()) return null;
  const v = value.trim();
  const slash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/.exec(v);
  if (slash) {
    const [, mm, dd, yyyy, hh, mi, ss] = slash;
    const t = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(mi), Number(ss));
    return Number.isNaN(t.getTime()) ? null : t.getTime();
  }
  const ms = Date.parse(v.replace(' ', 'T'));
  return Number.isNaN(ms) ? null : ms;
}

function getTicketOpenedTimestamp(ticket: Pick<OpenTicket, 'sysCreatedOn' | 'openedAt'>): number | null {
  return parseIncidentTicketDate(ticket.sysCreatedOn) ?? parseIncidentTicketDate(ticket.openedAt);
}

type TicketAssignmentHighlight = 'assigned' | 'unassigned' | 'unassigned_stale';

function getTicketAssignmentHighlight(ticket: OpenTicket): TicketAssignmentHighlight {
  if (ticket.assignedTo?.trim()) return 'assigned';
  const opened = getTicketOpenedTimestamp(ticket);
  if (opened == null) return 'unassigned';
  const ageDays = (Date.now() - opened) / (86_400 * 1000);
  return ageDays > RED_AFTER_DAYS_IF_UNASSIGNED ? 'unassigned_stale' : 'unassigned';
}

/** Whole days since open; used for unassigned ticket tooltips. */
function getDaysSinceTicketOpened(ticket: OpenTicket): number | null {
  const opened = getTicketOpenedTimestamp(ticket);
  if (opened == null) return null;
  return Math.max(0, Math.floor((Date.now() - opened) / (86_400 * 1000)));
}

function getUnassignedTicketTooltip(ticket: OpenTicket): ReactNode {
  const openedLabel = ticket.sysCreatedOn || ticket.openedAt || '—';
  const days = getDaysSinceTicketOpened(ticket);
  if (days != null) {
    const dayLine =
      days === 0
        ? 'Not assigned yet (opened today).'
        : `Not assigned for ${days} day${days === 1 ? '' : 's'}.`;
    return (
      <Box sx={{ maxWidth: 300, py: 0.25 }}>
        <Typography variant="body2" component="div">
          Ticket opened: {openedLabel}
        </Typography>
        <Typography variant="body2" component="div" sx={{ mt: 0.75, fontWeight: 600 }}>
          {dayLine}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ maxWidth: 280 }}>
      <Typography variant="body2">
        Ticket opened: {openedLabel}. No assignee yet.
      </Typography>
    </Box>
  );
}

/** Semantic fill colors for ServiceNow-style incident state labels (case-insensitive). */
type TicketStateChipPalette = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

function getOpenTicketStateChipColor(stateRaw: unknown): TicketStateChipPalette {
  const s = String(stateRaw ?? '').trim().toLowerCase();
  if (!s) return 'default';

  const exact: Record<string, TicketStateChipPalette> = {
    assigned: 'primary',
    open: 'info',
    'in progress': 'warning',
    'work in progress': 'warning',
    resolved: 'success',
    closed: 'default',
    canceled: 'default',
    cancelled: 'default',
    'on hold': 'secondary',
    pending: 'warning',
    'awaiting assignment': 'secondary',
  };

  if (exact[s]) return exact[s];
  if (s.includes('progress')) return 'warning';
  if (s.includes('hold')) return 'secondary';
  if (s.includes('pending')) return 'warning';
  if (s.includes('resolved')) return 'success';
  if (s.includes('clos') || s.includes('cancel')) return 'default';

  return 'secondary';
}

export default function Dashboard() {
  const { value: timeRange, setValue: setTimeRange } = useUrlState('days', TIME_RANGES.THIRTY_DAYS);
  const { value: activeTab, setValue: setActiveTab } = useUrlState('tab', 'overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'job' | 'organization'>('job');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [triggering, setTriggering] = useState(false);
  
  const { data, loading, error, refresh, viewType, setViewType } = useDashboardContext();
  const { hasPermission } = useAuthentication();
  
  const canTriggerWorkflow = hasPermission(PERMISSIONS.TRIGGER_WORKFLOWS);
  
  const handleTriggerWorkflow = async () => {
    try {
      setTriggering(true);
      await dashboardService.triggerDashboardWorkflow();
      // Refresh dashboard data after triggering
      await refresh(timeRange);
    } catch (err) {
      console.error('Failed to trigger workflow:', err);
    } finally {
      setTriggering(false);
    }
  };
  const lastTimeRangeRef = useRef<number | null>(null);
  const refreshRef = useRef(refresh);

  // Keep refresh ref up to date
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    if (timeRange && timeRange !== lastTimeRangeRef.current) {
      lastTimeRangeRef.current = timeRange;
      refreshRef.current(timeRange);
    }
  }, [timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle hash navigation on mount and when tab changes
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && activeTab) {
      // Wait for tab content to render
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 200);
    }
  }, [activeTab]);

  // Handle hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const metrics = data?.metrics;

  const handleTimeRangeChange = (_: unknown, value: number | null) => {
    if (value) setTimeRange(value);
  };

  // Filter function for jobs
  const filterJob = useCallback((job: HungJob | EarlyFailureJob | LowParityJob, query: string): boolean => {
    if (!query) return true;
    const searchTerm = query.toLowerCase();
    const status = 'status' in job ? job.status : undefined;
    return !!(
      job.transactionId?.toLowerCase().includes(searchTerm) ||
      job.sourceOrgName?.toLowerCase().includes(searchTerm) ||
      job.destinationOrgName?.toLowerCase().includes(searchTerm) ||
      job.sourceOrgId?.toLowerCase().includes(searchTerm) ||
      job.destinationOrgId?.toLowerCase().includes(searchTerm) ||
      (status && status.toLowerCase().includes(searchTerm))
    );
  }, []);

  // Filter function for organizations
  const filterOrganization = useCallback((org: OrganizationDetail, query: string): boolean => {
    if (!query) return true;
    const searchTerm = query.toLowerCase();
    return (
      org.orgName?.toLowerCase().includes(searchTerm) ||
      org.orgId?.toLowerCase().includes(searchTerm) ||
      org.orgRegion?.toLowerCase().includes(searchTerm)
    );
  }, []);

  // Filter function for org pairs
  const filterOrgPair = useCallback((pair: OrgPairAnalyticsItem, query: string): boolean => {
    if (!query) return true;
    const searchTerm = query.toLowerCase();
    return (
      pair.sourceOrgName?.toLowerCase().includes(searchTerm) ||
      pair.destinationOrgName?.toLowerCase().includes(searchTerm) ||
      pair.sourceOrgId?.toLowerCase().includes(searchTerm) ||
      pair.destinationOrgId?.toLowerCase().includes(searchTerm)
    );
  }, []);

  // Filter function for users by organization
  const filterUsersByOrg = useCallback((userOrg: UsersByOrganization, query: string): boolean => {
    if (!query) return true;
    const searchTerm = query.toLowerCase();
    return (
      userOrg.orgName?.toLowerCase().includes(searchTerm) ||
      userOrg.orgId?.toLowerCase().includes(searchTerm) ||
      (userOrg.users as Array<{ userEmail: string; userName: string }>)?.some(
        (user) => user.userEmail?.toLowerCase().includes(searchTerm) || user.userName?.toLowerCase().includes(searchTerm)
      )
    );
  }, []);

  // Filter function for schedules
  const filterSchedule = useCallback((schedule: Schedule, query: string): boolean => {
    if (!query) return true;
    const searchTerm = query.toLowerCase();
    return (
      schedule.cronName?.toLowerCase().includes(searchTerm) ||
      schedule.sourceName?.toLowerCase().includes(searchTerm) ||
      schedule.destinationName?.toLowerCase().includes(searchTerm) ||
      schedule.cron?.toLowerCase().includes(searchTerm)
    );
  }, []);

  // Filter function for subscriptions
  const filterSubscription = useCallback((subscription: ActiveSubscription | ErrorSubscription | EndingSoonSubscription, query: string): boolean => {
    if (!query) return true;
    const searchTerm = query.toLowerCase();
    return !!(
      subscription.orgName?.toLowerCase().includes(searchTerm) ||
      subscription.orgId?.toLowerCase().includes(searchTerm) ||
      subscription.subscriptionId?.toLowerCase().includes(searchTerm) ||
      subscription.region?.toLowerCase().includes(searchTerm) ||
      subscription.message?.toLowerCase().includes(searchTerm) ||
      ('parentOrgName' in subscription && subscription.parentOrgName?.toLowerCase().includes(searchTerm))
    );
  }, []);

  const filterOpenTicket = useCallback((ticket: OpenTicket, query: string): boolean => {
    if (!query) return true;
    const searchTerm = query.toLowerCase();
    return !!(
      ticket.number?.toLowerCase().includes(searchTerm) ||
      ticket.shortDescription?.toLowerCase().includes(searchTerm) ||
      ticket.state?.toLowerCase().includes(searchTerm) ||
      ticket.priority?.toLowerCase().includes(searchTerm) ||
      ticket.openedBy?.toLowerCase().includes(searchTerm) ||
      ticket.assignedTo?.toLowerCase().includes(searchTerm) ||
      ticket.callerId?.toLowerCase().includes(searchTerm) ||
      ticket.assignmentGroup?.toLowerCase().includes(searchTerm) ||
      ticket.category?.toLowerCase().includes(searchTerm) ||
      ticket.cmdbCi?.toLowerCase().includes(searchTerm) ||
      ticket.businessService?.toLowerCase().includes(searchTerm) ||
      ticket.sysUpdatedBy?.toLowerCase().includes(searchTerm)
    );
  }, []);

  // Generate suggestions based on search query and type
  const suggestions = useMemo(() => {
    if (!metrics || !searchQuery || searchQuery.length < 2) return [];
    
    const results: Array<{ id: string; title: string; subtitle: string; type: 'job' | 'organization'; searchTerm: string }> = [];
    
    if (searchType === 'job' || searchType === 'organization') {
      // Search jobs
      if (searchType === 'job') {
        const allJobs = [
          ...(metrics.hungJobs || []),
          ...(metrics.earlyFailureJobs || []),
          ...(metrics.lowParityJobs || []),
        ];
        
        allJobs.forEach((job) => {
          if (filterJob(job, searchQuery)) {
            const jobType = metrics.hungJobs?.some(j => j.transactionId === job.transactionId) ? 'Hung' :
                           metrics.earlyFailureJobs?.some(j => j.transactionId === job.transactionId) ? 'Failed' : 'Low Parity';
            // Use transaction ID or org names for filtering
            const filterTerm = job.transactionId || job.sourceOrgName || job.destinationOrgName || '';
            results.push({
              id: job.transactionId,
              title: `${jobType} Job: ${job.transactionId.substring(0, 20)}...`,
              subtitle: `${job.sourceOrgName} → ${job.destinationOrgName}`,
              type: 'job',
              searchTerm: filterTerm,
            });
          }
        });
      }
      
      // Search organizations
      if (searchType === 'organization') {
        metrics.activeCustomerOrgs?.organizationDetails?.forEach((org) => {
          if (filterOrganization(org, searchQuery)) {
            // Use org name or ID for filtering
            const filterTerm = org.orgName || org.orgId || '';
            results.push({
              id: org.orgId,
              title: org.orgName,
              subtitle: `Organization • ${org.orgRegion || 'N/A'} • ID: ${org.orgId}`,
              type: 'organization',
              searchTerm: filterTerm,
            });
          }
        });
        
        // Also include org pairs
        metrics.orgPairAnalytics?.items?.forEach((pair) => {
          if (filterOrgPair(pair, searchQuery)) {
            // Use source or destination org name for filtering
            const filterTerm = pair.sourceOrgName || pair.destinationOrgName || '';
            results.push({
              id: `${pair.sourceOrgId}-${pair.destinationOrgId}`,
              title: `${pair.sourceOrgName} → ${pair.destinationOrgName}`,
              subtitle: `Org Pair • ${pair.jobCount} jobs • ${pair.averageParityPercentage}% parity`,
              type: 'organization',
              searchTerm: filterTerm,
            });
          }
        });
      }
    }
    
    return results.slice(0, 10); // Limit to 10 suggestions
  }, [metrics, searchQuery, searchType, filterJob, filterOrganization, filterOrgPair]);

  // Filtered data - filter all data when search query exists
  const filteredMetrics = useMemo(() => {
    if (!metrics) return null;
    
    if (!searchQuery) {
      // No search query, return all data
      return metrics;
    }
    
    // Filter all data when search query exists
    const filteredHungJobs = metrics.hungJobs?.filter((job) => filterJob(job, searchQuery)) ?? [];
    const filteredEarlyFailureJobs = metrics.earlyFailureJobs?.filter((job) => filterJob(job, searchQuery)) ?? [];
    const filteredLowParityJobs = metrics.lowParityJobs?.filter((job) => filterJob(job, searchQuery)) ?? [];
    const allFilteredJobs = [...filteredHungJobs, ...filteredEarlyFailureJobs, ...filteredLowParityJobs];
    
    return {
      ...metrics,
      // Filter all job types
      hungJobs: filteredHungJobs,
      earlyFailureJobs: filteredEarlyFailureJobs,
      lowParityJobs: filteredLowParityJobs,
      // Recalculate sync job volume based on filtered jobs
      syncJobVolume: {
        ...metrics.syncJobVolume,
        totalJobs: allFilteredJobs.length,
        // Note: adHocJobs and scheduledJobs are not easily determinable from filtered data
        // Keeping original values for now
      },
      // Filter organizations
      activeCustomerOrgs: {
        ...metrics.activeCustomerOrgs,
        totalActiveOrgs: (metrics.activeCustomerOrgs?.organizationDetails?.filter((org) => filterOrganization(org, searchQuery)) ?? []).length,
        organizationDetails: metrics.activeCustomerOrgs?.organizationDetails?.filter((org) => filterOrganization(org, searchQuery)) ?? [],
      },
      // Filter org pairs
      orgPairAnalytics: {
        ...metrics.orgPairAnalytics,
        items: metrics.orgPairAnalytics?.items?.filter((pair) => filterOrgPair(pair, searchQuery)) ?? [],
      },
      // Filter users by organization
      usersByOrganization: metrics.usersByOrganization?.filter((userOrg) => filterUsersByOrg(userOrg, searchQuery)) ?? [],
      // Filter schedules
      activeSchedules: {
        ...metrics.activeSchedules,
        count: (metrics.activeSchedules?.schedules?.filter((schedule) => filterSchedule(schedule, searchQuery)) ?? []).length,
        schedules: metrics.activeSchedules?.schedules?.filter((schedule) => filterSchedule(schedule, searchQuery)) ?? [],
      },
      staleSchedules: {
        ...metrics.staleSchedules,
        count: (metrics.staleSchedules?.schedules?.filter((schedule) => filterSchedule(schedule, searchQuery)) ?? []).length,
        schedules: metrics.staleSchedules?.schedules?.filter((schedule) => filterSchedule(schedule, searchQuery)) ?? [],
      },
      // Filter subscriptions
      activeSubscriptions: metrics.activeSubscriptions?.filter((sub) => filterSubscription(sub, searchQuery)) ?? [],
      errorSubscriptions: metrics.errorSubscriptions?.filter((sub) => filterSubscription(sub, searchQuery)) ?? [],
      endingSoonSubscriptions: metrics.endingSoonSubscriptions?.filter((sub) => filterSubscription(sub, searchQuery)) ?? [],
      // Update subscriptions summary based on filtered data
      subscriptionsSummary: {
        ...metrics.subscriptionsSummary,
        totalActiveOrgs: (metrics.activeSubscriptions?.filter((sub) => filterSubscription(sub, searchQuery)) ?? []).length,
        totalErrorOrgs: (metrics.errorSubscriptions?.filter((sub) => filterSubscription(sub, searchQuery)) ?? []).length,
        totalEndingSoonOrgs: (metrics.endingSoonSubscriptions?.filter((sub) => filterSubscription(sub, searchQuery)) ?? []).length,
        totalProcessedOrgs: (metrics.activeSubscriptions?.filter((sub) => filterSubscription(sub, searchQuery)) ?? []).length +
                           (metrics.errorSubscriptions?.filter((sub) => filterSubscription(sub, searchQuery)) ?? []).length +
                           (metrics.endingSoonSubscriptions?.filter((sub) => filterSubscription(sub, searchQuery)) ?? []).length,
      },
      openTickets: (metrics.openTickets ?? []).filter((t) => filterOpenTicket(t, searchQuery)),
    };
  }, [metrics, searchQuery, filterJob, filterOrganization, filterOrgPair, filterUsersByOrg, filterSchedule, filterSubscription, filterOpenTicket]);

  const openTicketStats = useMemo(() => {
    const list = filteredMetrics?.openTickets ?? [];
    let unassigned = 0;
    let unassignedStale = 0;
    for (const t of list) {
      const h = getTicketAssignmentHighlight(t);
      if (h !== 'assigned') unassigned += 1;
      if (h === 'unassigned_stale') unassignedStale += 1;
    }
    return { total: list.length, unassigned, unassignedStale };
  }, [filteredMetrics?.openTickets]);

  /** Home overview card: largest number is total; unassigned breakdown below when any are unassigned. */
  const openTicketsHomeCard = useMemo(() => {
    const s = openTicketStats;
    const hasUnassigned = s.unassigned > 0;
    const color =
      s.unassignedStale > 0 ? '#ef4444' : hasUnassigned ? '#f59e0b' : '#0ea5e9';
    const accentBorder = hasUnassigned ? color : undefined;

    let subtitle: ReactNode;
    if (!hasUnassigned) {
      subtitle =
        s.total === 0
          ? 'No tickets in list'
          : 'All ticket types have assignees';
    } else {
      subtitle = `${s.unassigned} not assigned`;
    }

    return { value: s.total, subtitle, color, accentBorder };
  }, [openTicketStats]);

  // Navigation helpers for clickable cards
  const navigateTo = useCallback((tab: string, hash?: string) => {
    setActiveTab(tab);
    if (hash) {
      // Use setTimeout to ensure tab is rendered before scrolling
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Update URL hash
          window.history.replaceState(null, '', `#${hash}`);
        }
      }, 100);
    }
  }, [setActiveTab]);

  // Format date range for display
  const formatDateRange = () => {
    if (!data?.dateRange) return '';
    const startDate = new Date(data.dateRange.start);
    const endDate = new Date(data.dateRange.end);
    const options: Intl.DateTimeFormatOptions = { month: 'numeric', day: 'numeric', year: 'numeric' };
    return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)} (${data.dateRange.days}d)`;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    if (inputRef.current) {
      setAnchorEl(inputRef.current);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    if (inputRef.current) {
      setAnchorEl(inputRef.current);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => setIsFocused(false), 200);
  };

  const handleSuggestionClick = (suggestion: { id: string; title: string; type: 'job' | 'organization'; searchTerm: string }) => {
    // Set search query to filter data, don't navigate
    setSearchQuery(suggestion.searchTerm);
    setIsFocused(false);
  };

  const open = Boolean(anchorEl) && isFocused && (searchQuery.length >= 2 || suggestions.length > 0);

  return (
    <Box>
      {loading && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200, height: 2 }} />}

      <PageHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h4">Health Dashboard</Typography>
            {data?.dateRange && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  fontWeight: 400,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 14 }} />
                {formatDateRange()}
              </Typography>
            )}
          </Box>
        }
        subtitle={data ? `Updated ${new Date(data.generatedAt).toLocaleString()}` : undefined}
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="search-type-label">Type</InputLabel>
              <Select
                labelId="search-type-label"
                value={searchType}
                label="Type"
                onChange={(e) => {
                  setSearchType(e.target.value as 'job' | 'organization');
                  setSearchQuery(''); // Clear search when type changes
                }}
                sx={{
                  bgcolor: 'background.paper',
                  height: '32px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider',
                  },
                }}
              >
                <MenuItem value="job">Jobs</MenuItem>
                <MenuItem value="organization">Organizations</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ position: 'relative' }}>
              <TextField
                inputRef={inputRef}
                size="small"
                placeholder={`Search ${searchType === 'job' ? 'jobs' : 'organizations'}...`}
                value={searchQuery}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setSearchQuery('')}
                        sx={{
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          padding: 0,
                          color: 'text.secondary',
                          '&:hover': { color: 'text.primary' },
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 280,
                  bgcolor: 'background.paper',
                  '& .MuiOutlinedInput-root': {
                    height: '32px',
                    '& fieldset': {
                      borderColor: 'divider',
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              <Popper
                open={open}
                anchorEl={anchorEl}
                placement="bottom-start"
                style={{ zIndex: 1300, width: anchorEl?.offsetWidth }}
                modifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 8],
                    },
                  },
                ]}
              >
                <ClickAwayListener onClickAway={() => setIsFocused(false)}>
                  <Paper
                    elevation={8}
                    sx={{
                      mt: 1,
                      maxHeight: 400,
                      width: '100%',
                      minWidth: 400,
                      overflow: 'auto',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                  >
                    {suggestions.length > 0 ? (
                      <>
                        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''} found
                          </Typography>
                        </Box>
                        <List sx={{ p: 0 }}>
                          {suggestions.map((suggestion, index) => (
                            <ListItem key={`${suggestion.type}-${suggestion.id}-${index}`} disablePadding>
                              <ListItemButton onClick={() => handleSuggestionClick(suggestion)}>
                                <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                                  {suggestion.type === 'job' ? <WorkIcon fontSize="small" /> : <BusinessIcon fontSize="small" />}
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {suggestion.title}
                                      </Typography>
                                      <Chip
                                        label={suggestion.type === 'job' ? 'Job' : 'Organization'}
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: '0.65rem',
                                          bgcolor: 'action.selected',
                                        }}
                                      />
                                    </Box>
                                  }
                                  secondary={
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {suggestion.subtitle}
                                    </Typography>
                                  }
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </>
                    ) : searchQuery.length >= 2 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No {searchType === 'job' ? 'jobs' : 'organizations'} found for "{searchQuery}"
                        </Typography>
                      </Box>
                    ) : null}
                  </Paper>
                </ClickAwayListener>
              </Popper>
            </Box>
            <ToggleButtonGroup 
              value={viewType} 
              exclusive 
              onChange={(_, value) => value && setViewType(value)} 
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  height: '32px',
                  px: 2,
                },
              }}
            >
              <Tooltip title="Customer analytical data only">
                <span>
                  <ToggleButton value="customers">Customers</ToggleButton>
                </span>
              </Tooltip>
              <Tooltip title="All data including Test and Dev orgs">
                <span>
                  <ToggleButton value="all">All</ToggleButton>
                </span>
              </Tooltip>
            </ToggleButtonGroup>
            <ToggleButtonGroup 
              value={timeRange} 
              exclusive 
              onChange={handleTimeRangeChange} 
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  height: '32px',
                  px: 2,
                },
              }}
            >
              <ToggleButton value={7}>7D</ToggleButton>
              <ToggleButton value={30}>30D</ToggleButton>
            </ToggleButtonGroup>
            {canTriggerWorkflow && (
              <Button
                variant="contained"
                size="small"
                startIcon={<RefreshIcon fontSize="small" />}
                disabled={loading || triggering}
                onClick={handleTriggerWorkflow}
                sx={{
                  height: '32px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {triggering ? 'Refreshing...' : 'Refresh Data'}
              </Button>
            )}
          </Box>
        }
      />

      {/* Search Active Indicator */}
      {searchQuery && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5, 
            mb: 2,
            px: 2,
            py: 1.5,
            bgcolor: 'rgba(16, 185, 129, 0.08)',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'rgba(16, 185, 129, 0.2)',
          }}
        >
          <SearchIcon sx={{ fontSize: 20, color: 'success.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
            Filtering all data for: "{searchQuery}"
          </Typography>
          <Button
            size="small"
            onClick={() => setSearchQuery('')}
            sx={{ ml: 'auto', minWidth: 'auto', px: 1 }}
          >
            Clear
          </Button>
        </Box>
      )}

      <TabNavigation tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          action={<Button color="inherit" size="small" onClick={() => refresh()}>Retry</Button>}
        >
          <Box>{error}</Box>
        </Alert>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <Stack gap={2.5}>
          {/* Overview Cards - Based on metricDescriptions */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 2 }}>
            {loading ? (
              <>
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
              </>
            ) : (
              <>
                <StatCard 
                  title="Open Tickets" 
                  value={openTicketsHomeCard.value} 
                  subtitle={openTicketsHomeCard.subtitle}
                  icon={<SupportAgentIcon fontSize="small" />} 
                  color={openTicketsHomeCard.color}
                  accentBorder={openTicketsHomeCard.accentBorder}
                  onClick={() => navigateTo('tickets', 'open-tickets')}
                  description={data?.metricDescriptions?.openTickets}
                />
                <StatCard 
                  title="Active Users" 
                  value={filteredMetrics?.activeUsers.count ?? 0} 
                  subtitle="Users who logged in" 
                  icon={<PeopleIcon fontSize="small" />} 
                  color="#23395d" 
                  onClick={() => navigateTo('users', 'users-by-organization')}
                  description={data?.metricDescriptions?.activeUsers}
                />
                <StatCard 
                  title="Active Customer Orgs" 
                  value={filteredMetrics?.activeCustomerOrgs.totalActiveOrgs ?? 0} 
                  subtitle="Customer organizations" 
                  icon={<BusinessIcon fontSize="small" />} 
                  color="#10b981" 
                  onClick={() => navigateTo('organizations', 'active-customer-orgs')}
                  description={data?.metricDescriptions?.activeCustomerOrgs}
                />
                <StatCard 
                  title="Sync Job Volume" 
                  value={filteredMetrics?.syncJobVolume.totalJobs ?? 0} 
                  subtitle={`${filteredMetrics?.syncJobVolume.adHocJobs ?? 0} ad-hoc • ${filteredMetrics?.syncJobVolume.scheduledJobs ?? 0} scheduled`} 
                  icon={<SyncIcon fontSize="small" />} 
                  color="#0ea5e9" 
                  onClick={() => navigateTo('jobs')}
                  description={data?.metricDescriptions?.syncJobVolume}
                />
                <StatCard 
                  title="Hung Jobs" 
                  value={filteredMetrics?.hungJobs.length ?? 0} 
                  subtitle="Jobs running >24 hours" 
                  icon={<WarningIcon fontSize="small" />} 
                  color={(filteredMetrics?.hungJobs.length ?? 0) > 0 ? '#ef4444' : '#10b981'} 
                  onClick={() => navigateTo('jobs', 'hung-jobs')}
                  description={data?.metricDescriptions?.hungJobs}
                />
                <StatCard 
                  title="Early Failure Jobs" 
                  value={filteredMetrics?.earlyFailureJobs.length ?? 0} 
                  subtitle="Failed before import" 
                  icon={<ErrorIcon fontSize="small" />} 
                  color={(filteredMetrics?.earlyFailureJobs.length ?? 0) > 0 ? '#ef4444' : '#10b981'} 
                  onClick={() => navigateTo('jobs', 'early-failures')}
                  description={data?.metricDescriptions?.earlyFailureJobs}
                />
                <StatCard 
                  title="Low Parity Jobs" 
                  value={filteredMetrics?.lowParityJobs.length ?? 0} 
                  subtitle="Below 95% threshold" 
                  icon={<WarningIcon fontSize="small" />} 
                  color={(filteredMetrics?.lowParityJobs.length ?? 0) > 0 ? '#f59e0b' : '#10b981'} 
                  onClick={() => navigateTo('jobs', 'low-parity-jobs')}
                  description={data?.metricDescriptions?.lowParityJobs}
                />
                <StatCard 
                  title="Org Pair Analytics" 
                  value={filteredMetrics?.orgPairAnalytics.items?.length ?? 0} 
                  subtitle="Source→destination pairs" 
                  icon={<BusinessIcon fontSize="small" />} 
                  color="#8b5cf6" 
                  onClick={() => navigateTo('organizations', 'org-pair-analytics')}
                  description={data?.metricDescriptions?.orgPairAnalytics}
                />
                <StatCard 
                  title="Active Schedules" 
                  value={filteredMetrics?.activeSchedules.count ?? 0} 
                  subtitle="Currently running syncs" 
                  icon={<ScheduleIcon fontSize="small" />} 
                  color="#10b981"
                  onClick={() => navigateTo('schedules', 'active-schedules')}
                  description={data?.metricDescriptions?.activeSchedules}
                />
                <StatCard 
                  title="Stale Schedules" 
                  value={filteredMetrics?.staleSchedules.count ?? 0} 
                  subtitle="Idle for >30 days" 
                  icon={<PauseIcon fontSize="small" />} 
                  color={(filteredMetrics?.staleSchedules.count ?? 0) > 0 ? '#f59e0b' : '#10b981'}
                  onClick={() => navigateTo('schedules', 'stale-schedules')}
                  description={data?.metricDescriptions?.staleSchedules}
                />
                <StatCard 
                  title="Scheduler Adoption" 
                  value={filteredMetrics?.schedulerAdoption.totalActiveSchedules ?? 0} 
                  subtitle={`${filteredMetrics?.schedulerAdoption.uniqueOrgPairsInvolved ?? 0} org pairs`} 
                  icon={<TrendingUpIcon fontSize="small" />} 
                  color="#0ea5e9"
                  onClick={() => navigateTo('schedules', 'active-schedules')}
                  description={data?.metricDescriptions?.schedulerAdoption}
                />
                <StatCard 
                  title="Active Subscriptions" 
                  value={filteredMetrics?.activeSubscriptions.length ?? 0} 
                  subtitle="Valid subscriptions" 
                  icon={<CardMembershipIcon fontSize="small" />} 
                  color="#10b981"
                  onClick={() => navigateTo('subscriptions', 'active-subscriptions')}
                  description={data?.metricDescriptions?.activeSubscriptions}
                />
                <StatCard 
                  title="Error Subscriptions" 
                  value={filteredMetrics?.errorSubscriptions.length ?? 0} 
                  subtitle="Validation errors" 
                  icon={<ErrorIcon fontSize="small" />} 
                  color={(filteredMetrics?.errorSubscriptions.length ?? 0) > 0 ? '#ef4444' : '#10b981'}
                  onClick={() => navigateTo('subscriptions', 'error-subscriptions')}
                  description={data?.metricDescriptions?.errorSubscriptions}
                />
                <StatCard 
                  title="Ending Soon Subscriptions" 
                  value={filteredMetrics?.endingSoonSubscriptions.length ?? 0} 
                  subtitle="Expiring within 3 months" 
                  icon={<WarningIcon fontSize="small" />} 
                  color={(filteredMetrics?.endingSoonSubscriptions.length ?? 0) > 0 ? '#f59e0b' : '#10b981'}
                  onClick={() => navigateTo('subscriptions', 'ending-soon-subscriptions')}
                  description={data?.metricDescriptions?.endingSoonSubscriptions}
                />
                <StatCard 
                  title="Subscriptions Summary" 
                  value={filteredMetrics?.subscriptionsSummary.totalActiveOrgs ?? 0} 
                  subtitle={`${filteredMetrics?.subscriptionsSummary.totalErrorOrgs ?? 0} errors • ${filteredMetrics?.subscriptionsSummary.totalEndingSoonOrgs ?? 0} ending soon`} 
                  icon={<CardMembershipIcon fontSize="small" />} 
                  color="#8b5cf6"
                  onClick={() => navigateTo('subscriptions')}
                  description={data?.metricDescriptions?.subscriptionsSummary}
                />
              </>
            )}
          </Box>
        </Stack>
      )}

      {/* Jobs Tab */}
      {activeTab === 'jobs' && (
        <Stack gap={2}>
          {/* Back Button */}
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigateTo('overview')}
              variant="outlined"
              size="small"
              sx={{ mb: 1 }}
            >
              Back to Overview
            </Button>
          </Box>
          
          {/* Sync Job Volume */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
            {loading ? (
              <><StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton /></>
            ) : (
              <>
                <StatCard
                  title="Total Sync Jobs"
                  value={filteredMetrics?.syncJobVolume.totalJobs ?? 0}
                  subtitle={`${filteredMetrics?.syncJobVolume.adHocJobs ?? 0} ad-hoc • ${filteredMetrics?.syncJobVolume.scheduledJobs ?? 0} scheduled`}
                  icon={<SyncIcon fontSize="small" />}
                  color="#0ea5e9"
                  description={data?.metricDescriptions?.syncJobVolume}
                />
                <StatCard
                  title="Ad-hoc Jobs"
                  value={filteredMetrics?.syncJobVolume.adHocJobs ?? 0}
                  subtitle="Manually triggered syncs"
                  icon={<WorkIcon fontSize="small" />}
                  color="#8b5cf6"
                />
                <StatCard
                  title="Scheduled Jobs"
                  value={filteredMetrics?.syncJobVolume.scheduledJobs ?? 0}
                  subtitle="Cron-based syncs"
                  icon={<ScheduleIcon fontSize="small" />}
                  color="#10b981"
                />
              </>
            )}
          </Box>

          {/* Job Issues Summary */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
            {loading ? (
              <><MetricCardSkeleton /><MetricCardSkeleton /><MetricCardSkeleton /></>
            ) : (
              <>
                <MetricCard 
                  title="Hung Jobs" 
                  value={filteredMetrics?.hungJobs.length ?? 0} 
                  subtitle="Jobs running >24 hours" 
                  status={(filteredMetrics?.hungJobs.length ?? 0) > 0 ? 'error' : 'success'}
                  chipLabel={(filteredMetrics?.hungJobs.length ?? 0) > 0 ? 'Critical' : 'None'}
                  onClick={() => navigateTo('jobs', 'hung-jobs')}
                  description={data?.metricDescriptions?.hungJobs}
                />
                <MetricCard 
                  title="Early Failures" 
                  value={filteredMetrics?.earlyFailureJobs.length ?? 0} 
                  subtitle="Failed before import" 
                  status={(filteredMetrics?.earlyFailureJobs.length ?? 0) > 0 ? 'error' : 'success'}
                  chipLabel={(filteredMetrics?.earlyFailureJobs.length ?? 0) > 0 ? 'Needs review' : 'None'}
                  onClick={() => navigateTo('jobs', 'early-failures')}
                  description={data?.metricDescriptions?.earlyFailureJobs}
                />
                <MetricCard 
                  title="Low Parity" 
                  value={filteredMetrics?.lowParityJobs.length ?? 0} 
                  subtitle="Below 95% threshold" 
                  status={(filteredMetrics?.lowParityJobs.length ?? 0) > 0 ? 'warning' : 'success'}
                  chipLabel={(filteredMetrics?.lowParityJobs.length ?? 0) > 0 ? 'Incomplete' : 'None'}
                  onClick={() => navigateTo('jobs', 'low-parity-jobs')}
                  description={data?.metricDescriptions?.lowParityJobs}
                />
              </>
            )}
          </Box>

          <>
              <Box id="hung-jobs" sx={{ scrollMarginTop: 20 }}>
                <DataGrid
                  title="Hung Jobs"
                  loading={loading}
                columns={[
                  { key: 'transactionId', label: 'Transaction ID', width: 300, render: (r) => (
                    <Tooltip title={r.transactionId as string} arrow>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {(r.transactionId as string)}
                      </Typography>
                    </Tooltip>
                  )},
                  { key: 'sourceOrgName', label: 'Source', width: 180 },
                  { key: 'destinationOrgName', label: 'Destination', width: 180 },
                  { key: 'dateStarted', label: 'Started', width: 150, render: (r) => {
                    const started = new Date(r.dateStarted as string);
                    const hoursAgo = Math.floor((Date.now() - started.getTime()) / (1000 * 60 * 60));
                    return (
                      <Tooltip title={started.toLocaleString()} arrow>
                        <Chip size="small" label={`${hoursAgo}h ago`} color="error" variant="outlined" />
                      </Tooltip>
                    );
                  }},
                  { key: 'status', label: 'Status', width: 120, render: (r) => (
                    <Chip size="small" label={r.status as string || 'running'} color="error" icon={<WarningIcon sx={{ fontSize: 14 }} />} />
                  )},
                  { key: 'actions', label: '', width: 120, render: (r) => (
                    <Tooltip title="View in Transaction Lookup">
                      <IconButton
                        component={Link}
                        to={`/log-oracle/transaction-lookup?it=${encodeURIComponent(r.transactionId as string)}`}
                        size="small"
                      >
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )},
                ]}
                data={(filteredMetrics?.hungJobs ?? []) as unknown as Record<string, unknown>[]}
                emptyMessage="No hung jobs - all syncs completing normally"
                emptyIcon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />}
                />
              </Box>
              <Box id="early-failures" sx={{ scrollMarginTop: 20 }}>
                <DataGrid
                  title="Early Failure Jobs"
                  loading={loading}
                columns={[
                  { key: 'transactionId', label: 'Transaction ID', width: 300, render: (r) => (
                    <Tooltip title={r.transactionId as string} arrow>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {(r.transactionId as string)}
                      </Typography>
                    </Tooltip>
                  )},
                  { key: 'sourceOrgName', label: 'Source', width: 180 },
                  { key: 'destinationOrgName', label: 'Destination', width: 180 },
                  { key: 'status', label: 'Status', width: 100, render: (r) => (
                    <Chip size="small" label={r.status as string || 'failed'} color="error" />
                  )},
                  { key: 'failedSteps', label: 'Failed Steps', width: 300, render: (r) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(r.failedSteps as string[]).map((step, idx) => (
                        <Chip key={idx} size="small" label={step} color="error" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                      ))}
                    </Box>
                  )},
                  { key: 'successfullSteps', label: 'Completed', width: 120, render: (r) => {
                    const steps = r.successfullSteps as string[] | undefined;
                    if (!steps || steps.length === 0) return '-';
                    return (
                      <Tooltip title={steps.join(', ')} arrow>
                        <Chip size="small" label={`${steps.length} steps`} color="success" variant="outlined" />
                      </Tooltip>
                    );
                  }},
                  { key: 'dateStarted', label: 'Started', width: 180, render: (r) => new Date(r.dateStarted as string).toLocaleString() },
                  { key: 'actions', label: '', width: 120, render: (r) => (
                    <Tooltip title="View in Transaction Lookup">
                      <IconButton
                        component={Link}
                        to={`/log-oracle/transaction-lookup?it=${encodeURIComponent(r.transactionId as string)}`}
                        size="small"
                      >
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )},
                ]}
                data={(filteredMetrics?.earlyFailureJobs ?? []) as unknown as Record<string, unknown>[]}
                emptyMessage="No early failures"
                emptyIcon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />}
                />
              </Box>
              <Box id="low-parity-jobs" sx={{ scrollMarginTop: 20 }}>
                <DataGrid
                  title="Low Parity Jobs"
                  loading={loading}
                columns={[
                  { key: 'transactionId', label: 'Transaction ID', width: 300, render: (r) => (
                    <Tooltip title={r.transactionId as string} arrow>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {(r.transactionId as string)}
                      </Typography>
                    </Tooltip>
                  )},
                  { key: 'sourceOrgName', label: 'Source', width: 180 },
                  { key: 'destinationOrgName', label: 'Destination', width: 180 },
                  { key: 'orgParityPercentage', label: 'Parity %', width: 220, render: (r) => {
                    const parity = parseFloat(r.orgParityPercentage as string);
                    return (
                      <Box sx={{ minWidth: 160 }}>
                        <ParityBar
                          copied={parity}
                          total={100}
                          isRunning={false}
                          totalBlocks={10}
                          showLoaderWhenRunning={false}
                        />
                      </Box>
                    );
                  }},
                  { key: 'dateStarted', label: 'Started', width: 180, render: (r) => new Date(r.dateStarted as string).toLocaleString() },
                  { key: 'actions', label: '', width: 120, render: (r) => (
                    <Tooltip title="View in Transaction Lookup">
                      <IconButton
                        component={Link}
                        to={`/log-oracle/transaction-lookup?it=${encodeURIComponent(r.transactionId as string)}`}
                        size="small"
                      >
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )},
                ]}
                data={(filteredMetrics?.lowParityJobs ?? []) as unknown as Record<string, unknown>[]}
                emptyMessage="All jobs have healthy parity (>95%)"
                emptyIcon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />}
                />
              </Box>
          </>
        </Stack>
      )}

      {/* Organizations Tab */}
      {activeTab === 'organizations' && (
        <Stack gap={2}>
          {/* Back Button */}
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigateTo('overview')}
              variant="outlined"
              size="small"
              sx={{ mb: 1 }}
            >
              Back to Overview
            </Button>
          </Box>
          
          <>
              <Box id="org-pair-analytics" sx={{ scrollMarginTop: 20 }}>
                <DataGrid
                  title="Org Pair Analytics"
                  loading={loading}
                columns={[
                  { key: 'sourceOrgName', label: 'Source', width: 180, render: (r) => (
                    <Tooltip title={`Region: ${r.sourceOrgRegion || 'N/A'}`} arrow placement="top">
                      <span>{r.sourceOrgName as string}</span>
                    </Tooltip>
                  )},
                  { key: 'destinationOrgName', label: 'Destination', width: 180, render: (r) => (
                    <Tooltip title={`Region: ${r.destinationOrgRegion || 'N/A'}`} arrow placement="top">
                      <span>{r.destinationOrgName as string}</span>
                    </Tooltip>
                  )},
                  { key: 'averageParityPercentage', label: 'Avg Parity', width: 200, render: (r) => (
                    <Box sx={{ minWidth: 140 }}>
                      <ParityBar
                        copied={parseFloat(String(r.averageParityPercentage))}
                        total={100}
                        isRunning={false}
                        totalBlocks={10}
                        showLoaderWhenRunning={false}
                      />
                    </Box>
                  )},
                  { key: 'p90DurationSeconds', label: 'P90 Duration', width: 130, render: (r) => {
                    const seconds = r.p90DurationSeconds as number;
                    if (seconds >= 3600) return `${(seconds / 3600).toFixed(1)}h`;
                    if (seconds >= 60) return `${Math.round(seconds / 60)}m`;
                    return `${Math.round(seconds)}s`;
                  }},
                  { key: 'jobCount', label: 'Jobs', width: 80 },
                  { key: 'lowParity', label: 'Status', width: 140, render: (r) => (
                    r.lowParity 
                      ? <Chip size="small" label="Low Parity" color="warning" variant="outlined" icon={<WarningIcon sx={{ fontSize: 14 }} />} />
                      : <Chip size="small" label="Healthy" color="success" variant="outlined" icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} />
                  )},
                ]}
                data={(filteredMetrics?.orgPairAnalytics.items ?? []) as unknown as Record<string, unknown>[]}
                emptyMessage="No data"
                getRowId={(row: Record<string, unknown>) => `${row.sourceOrgId}-${row.destinationOrgId}`}
                />
              </Box>
              <Box id="active-customer-orgs" sx={{ scrollMarginTop: 20 }}>
                <DataGrid
                  title="Active Customer Organizations"
                  loading={loading}
                columns={[
                  { key: 'orgName', label: 'Organization', width: 200 },
                  { key: 'orgId', label: 'Organization ID', width: 320, render: (r) => (
                    <Tooltip title="Click to copy" arrow>
                      <Typography 
                        variant="body2" 
                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }}
                        onClick={() => navigator.clipboard.writeText(r.orgId as string)}
                      >
                        {r.orgId as string}
                      </Typography>
                    </Tooltip>
                  )},
                  { key: 'orgRegion', label: 'Region', width: 140, render: (r) => <Chip size="small" label={r.orgRegion as string} variant="outlined" /> },
                ]}
                data={(filteredMetrics?.activeCustomerOrgs.organizationDetails ?? []) as unknown as Record<string, unknown>[]}
                emptyMessage="No organizations"
                getRowId={(row: Record<string, unknown>) => row.orgId as string}
                />
              </Box>
          </>
        </Stack>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <Stack gap={2}>
          {/* Back Button */}
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigateTo('overview')}
              variant="outlined"
              size="small"
              sx={{ mb: 1 }}
            >
              Back to Overview
            </Button>
          </Box>
          
          {/* Subscriptions Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
            {loading ? (
              <><StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton /></>
            ) : (
              <>
                <StatCard 
                  title="Active Subscriptions" 
                  value={filteredMetrics?.activeSubscriptions.length ?? 0} 
                  subtitle="Valid subscriptions" 
                  icon={<CardMembershipIcon fontSize="small" />} 
                  color="#10b981"
                  onClick={() => navigateTo('subscriptions', 'active-subscriptions')}
                  description={data?.metricDescriptions?.activeSubscriptions}
                />
                <StatCard 
                  title="Error Subscriptions" 
                  value={filteredMetrics?.errorSubscriptions.length ?? 0} 
                  subtitle="Validation errors" 
                  icon={<ErrorIcon fontSize="small" />} 
                  color={(filteredMetrics?.errorSubscriptions.length ?? 0) > 0 ? '#ef4444' : '#10b981'}
                  onClick={() => navigateTo('subscriptions', 'error-subscriptions')}
                  description={data?.metricDescriptions?.errorSubscriptions}
                />
                <StatCard 
                  title="Ending Soon" 
                  value={filteredMetrics?.endingSoonSubscriptions.length ?? 0} 
                  subtitle="Expiring within 3 months" 
                  icon={<WarningIcon fontSize="small" />} 
                  color={(filteredMetrics?.endingSoonSubscriptions.length ?? 0) > 0 ? '#f59e0b' : '#10b981'}
                  onClick={() => navigateTo('subscriptions', 'ending-soon-subscriptions')}
                  description={data?.metricDescriptions?.endingSoonSubscriptions}
                />
                <StatCard 
                  title="Total Processed" 
                  value={filteredMetrics?.subscriptionsSummary.totalProcessedOrgs ?? 0} 
                  subtitle={`${filteredMetrics?.subscriptionsSummary.totalActiveOrgs ?? 0} active • ${filteredMetrics?.subscriptionsSummary.totalErrorOrgs ?? 0} errors`} 
                  icon={<CardMembershipIcon fontSize="small" />} 
                  color="#8b5cf6"
                  description={data?.metricDescriptions?.subscriptionsSummary}
                />
              </>
            )}
          </Box>

          {/* Subscription Tables */}
          <>
              <Box id="active-subscriptions" sx={{ scrollMarginTop: 20 }}>
                <DataGrid
                  title="Active Subscriptions"
                  loading={loading}
                  columns={[
                    { key: 'orgName', label: 'Organization', width: 200 },
                    { key: 'orgId', label: 'Organization ID', width: 280, render: (r) => (
                      <Tooltip title="Click to copy" arrow>
                        <Typography 
                          variant="body2" 
                          sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }}
                          onClick={() => navigator.clipboard.writeText(r.orgId as string)}
                        >
                          {r.orgId as string}
                        </Typography>
                      </Tooltip>
                    )},
                    { key: 'subscriptionId', label: 'Subscription ID', width: 280, render: (r) => (
                      <Tooltip title="Click to copy" arrow>
                        <Typography 
                          variant="body2" 
                          sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }}
                          onClick={() => navigator.clipboard.writeText(r.subscriptionId as string)}
                        >
                          {r.subscriptionId as string}
                        </Typography>
                      </Tooltip>
                    )},
                    { key: 'region', label: 'Region', width: 140, render: (r) => <Chip size="small" label={r.region as string} variant="outlined" /> },
                    { key: 'status', label: 'Status', width: 100, render: (r) => (
                      <Chip size="small" label={r.status as number} color="success" />
                    )},
                    { key: 'message', label: 'Message', width: 300, render: (r) => (
                      <Tooltip title={r.message as string} arrow>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }} noWrap>
                          {r.message as string}
                        </Typography>
                      </Tooltip>
                    )},
                    { key: 'parentOrgName', label: 'Parent Org', width: 180, render: (r) => {
                      const sub = r as unknown as ActiveSubscription;
                      if (!sub.parentOrgName) return '-';
                      return (
                        <Tooltip title={`Parent: ${sub.parentOrgName} (${sub.parentRegion || 'N/A'})`} arrow>
                          <Chip size="small" label={sub.parentOrgName} variant="outlined" />
                        </Tooltip>
                      );
                    }},
                  ]}
                  data={(filteredMetrics?.activeSubscriptions ?? []) as unknown as Record<string, unknown>[]}
                  emptyMessage="No active subscriptions"
                  emptyIcon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />}
                  getRowId={(row: Record<string, unknown>) => row.subscriptionId as string}
                />
              </Box>
              <Box id="error-subscriptions" sx={{ scrollMarginTop: 20 }}>
                <DataGrid
                  title="Error Subscriptions"
                  loading={loading}
                  columns={[
                    { key: 'orgName', label: 'Organization', width: 200 },
                    { key: 'orgId', label: 'Organization ID', width: 280, render: (r) => (
                      <Tooltip title="Click to copy" arrow>
                        <Typography 
                          variant="body2" 
                          sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }}
                          onClick={() => navigator.clipboard.writeText(r.orgId as string)}
                        >
                          {r.orgId as string}
                        </Typography>
                      </Tooltip>
                    )},
                    { key: 'subscriptionId', label: 'Subscription ID', width: 280, render: (r) => (
                      <Tooltip title="Click to copy" arrow>
                        <Typography 
                          variant="body2" 
                          sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }}
                          onClick={() => navigator.clipboard.writeText(r.subscriptionId as string)}
                        >
                          {r.subscriptionId as string}
                        </Typography>
                      </Tooltip>
                    )},
                    { key: 'region', label: 'Region', width: 140, render: (r) => <Chip size="small" label={r.region as string} variant="outlined" /> },
                    { key: 'status', label: 'Status', width: 100, render: (r) => (
                      <Chip size="small" label={r.status as number} color="error" />
                    )},
                    { key: 'message', label: 'Error Message', width: 400, render: (r) => (
                      <Tooltip title={r.message as string} arrow>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'error.main' }} noWrap>
                          {r.message as string}
                        </Typography>
                      </Tooltip>
                    )},
                  ]}
                  data={(filteredMetrics?.errorSubscriptions ?? []) as unknown as Record<string, unknown>[]}
                  emptyMessage="No subscription errors - all subscriptions are valid!"
                  emptyIcon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />}
                  getRowId={(row: Record<string, unknown>) => row.subscriptionId as string}
                />
              </Box>
              <Box id="ending-soon-subscriptions" sx={{ scrollMarginTop: 20 }}>
                <DataGrid
                  title="Ending Soon Subscriptions"
                  loading={loading}
                  columns={[
                    { key: 'orgName', label: 'Organization', width: 200 },
                    { key: 'orgId', label: 'Organization ID', width: 280, render: (r) => (
                      <Tooltip title="Click to copy" arrow>
                        <Typography 
                          variant="body2" 
                          sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }}
                          onClick={() => navigator.clipboard.writeText(r.orgId as string)}
                        >
                          {r.orgId as string}
                        </Typography>
                      </Tooltip>
                    )},
                    { key: 'subscriptionId', label: 'Subscription ID', width: 280, render: (r) => (
                      <Tooltip title="Click to copy" arrow>
                        <Typography 
                          variant="body2" 
                          sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }}
                          onClick={() => navigator.clipboard.writeText(r.subscriptionId as string)}
                        >
                          {r.subscriptionId as string}
                        </Typography>
                      </Tooltip>
                    )},
                    { key: 'subscriptionEndDate', label: 'End Date', width: 160, render: (r) => {
                      const endDate = r.subscriptionEndDate as string | undefined;
                      if (!endDate) return '-';
                      const date = new Date(endDate);
                      return (
                        <Tooltip title={date.toLocaleString()} arrow>
                          <Chip size="small" label={date.toLocaleDateString()} color="warning" variant="outlined" />
                        </Tooltip>
                      );
                    }},
                    { key: 'region', label: 'Region', width: 140, render: (r) => <Chip size="small" label={r.region as string} variant="outlined" /> },
                    { key: 'status', label: 'Status', width: 100, render: (r) => (
                      <Chip size="small" label={r.status as number} color="warning" />
                    )},
                    { key: 'message', label: 'Message', width: 300, render: (r) => (
                      <Tooltip title={r.message as string} arrow>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }} noWrap>
                          {r.message as string}
                        </Typography>
                      </Tooltip>
                    )},
                    { key: 'parentOrgName', label: 'Parent Org', width: 180, render: (r) => {
                      const sub = r as unknown as EndingSoonSubscription;
                      if (!sub.parentOrgName) return '-';
                      return (
                        <Tooltip title={`Parent: ${sub.parentOrgName} (${sub.parentRegion || 'N/A'})`} arrow>
                          <Chip size="small" label={sub.parentOrgName} variant="outlined" />
                        </Tooltip>
                      );
                    }},
                  ]}
                  data={(filteredMetrics?.endingSoonSubscriptions ?? []) as unknown as Record<string, unknown>[]}
                  emptyMessage="No subscriptions ending soon - all subscriptions are healthy!"
                  emptyIcon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />}
                  getRowId={(row: Record<string, unknown>) => row.subscriptionId as string}
                />
              </Box>
          </>
        </Stack>
      )}

      {/* Tickets tab */}
      {activeTab === 'tickets' && (
        <Stack gap={2}>
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigateTo('overview')}
              variant="outlined"
              size="small"
              sx={{ mb: 1 }}
            >
              Back to Overview
            </Button>
          </Box>

          <Box id="open-tickets" sx={{ scrollMarginTop: 20 }}>
            <DataGrid
              title="Open Tickets"
              loading={loading}
              showToolbarFilterPanelButton={false}
              showToolbarColumnsButton={false}
              showToolbarExport={false}
              disableRowSelectionOnClick
              getRowClassName={(params) => {
                const row = params.row as unknown as OpenTicket;
                const h = getTicketAssignmentHighlight(row);
                if (h === 'unassigned_stale') return 'ops-ticket--stale-unassigned';
                if (h === 'unassigned') return 'ops-ticket--unassigned';
                return '';
              }}
              gridSx={(theme) => {
                const warning = theme.palette.warning.main;
                const error = theme.palette.error.main;
                return {
                  '& .MuiDataGrid-row.ops-ticket--stale-unassigned': {
                    backgroundColor: alpha(error, 0.12),
                    '&:hover': {
                      backgroundColor: alpha(error, 0.18),
                    },
                  },
                  '& .MuiDataGrid-row.ops-ticket--unassigned': {
                    backgroundColor: alpha(warning, 0.1),
                    '&:hover': {
                      backgroundColor: alpha(warning, 0.16),
                    },
                  },
                };
              }}
              columns={[
                {
                  key: 'number',
                  label: 'Ticket #',
                  width: 148,
                  render: (r) => {
                    const portalUrl = getServiceNowIncidentPortalUrlFromTicketRow(
                      r as Record<string, unknown>,
                    );
                    if (portalUrl) {
                      return (
                        <Box
                          component="a"
                          href={portalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.35,
                            color: 'primary.main',
                            maxWidth: '100%',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {(r.number as string) || '—'}
                          <OpenInNewIcon sx={{ fontSize: 13, opacity: 0.85 }} aria-hidden />
                        </Box>
                      );
                    }
                    return (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>
                        {(r.number as string) || '—'}
                      </Typography>
                    );
                  },
                },
                {
                  key: 'shortDescription',
                  label: 'Short description',
                  minWidth: 220,
                  flex: 1,
                  render: (r) => (
                    <Tooltip title={r.shortDescription as string} arrow placement="top-start">
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }} noWrap>
                        {(r.shortDescription as string) || '—'}
                      </Typography>
                    </Tooltip>
                  ),
                },
                {
                  key: 'state',
                  label: 'State',
                  width: 120,
                  render: (r) => {
                    const label = (r.state as string)?.trim() || '—';
                    const sNorm = String(r.state ?? '').trim().toLowerCase();
                    if (sNorm === 'new') {
                      return (
                        <Chip
                          size="small"
                          label={label}
                          variant="outlined"
                          sx={(theme) => ({
                            fontWeight: 600,
                            borderWidth: 2,
                            borderColor: alpha(theme.palette.error.main, 0.42),
                            backgroundColor: alpha(theme.palette.error.main, 0.12),
                            color:
                              theme.palette.mode === 'dark'
                                ? theme.palette.error.light
                                : theme.palette.error.dark,
                            '& .MuiChip-label': { px: 1, fontSize: '0.7rem' },
                          })}
                        />
                      );
                    }
                    const color = getOpenTicketStateChipColor(r.state);
                    return (
                      <Chip
                        size="small"
                        label={label}
                        color={color}
                        variant="outlined"
                        sx={{
                          fontWeight: 600,
                          borderWidth: 2,
                          '& .MuiChip-label': { px: 1, fontSize: '0.7rem' },
                        }}
                      />
                    );
                  },
                },
                { key: 'openedAt', label: 'Opened', width: 155 },
                { key: 'sysUpdatedOn', label: 'Updated on', width: 160 },
                { key: 'sysUpdatedBy', label: 'Updated by', width: 130 },
                { key: 'openedBy', label: 'Opened by', width: 130 },
                {
                  key: 'assignedTo',
                  label: 'Assigned to',
                  width: 230,
                  render: (r) => {
                    const ticket = r as unknown as OpenTicket;
                    const h = getTicketAssignmentHighlight(ticket);
                    if (h === 'assigned') {
                      return (
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {ticket.assignedTo}
                        </Typography>
                      );
                    }
                    const daysOpen = getDaysSinceTicketOpened(ticket);
                    const chipLabel =
                      daysOpen != null ? `Unassigned · ${daysOpen}d` : 'Unassigned';
                    return (
                      <Tooltip title={getUnassignedTicketTooltip(ticket)} placement="top" arrow>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            maxWidth: '100%',
                            cursor: 'help',
                          }}
                        >
                          <Chip
                            size="small"
                            label={chipLabel}
                            color={h === 'unassigned_stale' ? 'error' : 'warning'}
                            variant="outlined"
                            sx={{ maxWidth: '100%', '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                          />
                        </Box>
                      </Tooltip>
                    );
                  },
                },
              ]}
              data={(filteredMetrics?.openTickets ?? []) as unknown as Record<string, unknown>[]}
              emptyMessage="No open tickets returned for this period"
              emptyIcon={<SupportAgentIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />}
              getRowId={(row: Record<string, unknown>) =>
                (typeof row.sysId === 'string' && row.sysId) ||
                (typeof row.sys_id === 'string' && row.sys_id) ||
                String(row.number)}
            />
          </Box>
        </Stack>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <Stack gap={2}>
          {/* Back Button */}
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigateTo('overview')}
              variant="outlined"
              size="small"
              sx={{ mb: 1 }}
            >
              Back to Overview
            </Button>
          </Box>
          
          {/* User Summary Card */}
          <Card>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">User Activity Summary</Typography>
                <Chip 
                  label={`${filteredMetrics?.activeUsers.count ?? 0} active users`} 
                  color="primary" 
                  size="small"
                  icon={<PeopleIcon sx={{ fontSize: 16 }} />}
                />
              </Box>
              {loading ? (
                <Box>
                  <Skeleton width={300} height={20} />
                  <Skeleton width={250} height={16} sx={{ mt: 0.5 }} />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {filteredMetrics?.usersByOrganization.length ?? 0} organizations have active users in this time period
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Users by Organization Table */}
          <Box id="users-by-organization" sx={{ scrollMarginTop: 20 }}>
            <DataGrid
              title="Users by Organization"
              loading={loading}
              columns={[
                { key: 'orgName', label: 'Organization', width: 200 },
                { key: 'activeUsers', label: 'Active Users', width: 130, render: (r) => (
                  <Chip size="small" label={r.activeUsers as number} color="primary" variant="outlined" />
                )},
                { key: 'users', label: 'User Details', width: 300, render: (r) => {
                  const users = r.users as Array<{ userEmail: string; userName: string }>;
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
                      {users.map((user, idx) => (
                        <Tooltip key={idx} title={user.userEmail} arrow placement="top">
                          <Chip 
                            size="small" 
                            label={user.userName} 
                            variant="outlined"
                            avatar={<Avatar sx={{ width: 20, height: 20, fontSize: '0.7rem' }}>{user.userName.charAt(0).toUpperCase()}</Avatar>}
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  );
                }},
                { key: 'orgId', label: 'Org ID', minWidth: 280, render: (r) => (
                  <Tooltip title="Click to copy" arrow>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace', 
                        fontSize: '0.75rem', 
                        color: 'text.secondary',
                        cursor: 'pointer',
                        '&:hover': { color: 'primary.main' }
                      }}
                      onClick={() => navigator.clipboard.writeText(r.orgId as string)}
                    >
                      {r.orgId as string}
                    </Typography>
                  </Tooltip>
                )},
              ]}
              data={(filteredMetrics?.usersByOrganization ?? []) as unknown as Record<string, unknown>[]}
              emptyMessage="No user activity data"
              emptyIcon={<PeopleIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />}
              getRowId={(row) => row.orgId as string}
            />
          </Box>
        </Stack>
      )}

      {/* Schedules Tab */}
      {activeTab === 'schedules' && (
        <Stack gap={2}>
          {/* Back Button */}
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigateTo('overview')}
              variant="outlined"
              size="small"
              sx={{ mb: 1 }}
            >
              Back to Overview
            </Button>
          </Box>
          
          {/* Schedule Summary */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
            {loading ? (
              <><StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton /></>
            ) : (
              <>
                <StatCard 
                  title="Active Schedules" 
                  value={filteredMetrics?.activeSchedules.count ?? 0} 
                  subtitle="Currently running syncs" 
                  icon={<ScheduleIcon fontSize="small" />} 
                  color="#10b981"
                  onClick={() => navigateTo('schedules', 'active-schedules')}
                  description={data?.metricDescriptions?.activeSchedules}
                />
                <StatCard 
                  title="Stale Schedules" 
                  value={filteredMetrics?.staleSchedules.count ?? 0} 
                  subtitle="Idle for >30 days" 
                  icon={<PauseIcon fontSize="small" />} 
                  color={(filteredMetrics?.staleSchedules.count ?? 0) > 0 ? '#f59e0b' : '#10b981'}
                  onClick={() => navigateTo('schedules', 'stale-schedules')}
                  description={data?.metricDescriptions?.staleSchedules}
                />
                <StatCard 
                  title="Org Pairs with Schedules" 
                  value={filteredMetrics?.schedulerAdoption.uniqueOrgPairsInvolved ?? 0} 
                  subtitle="Unique source→destination pairs" 
                  icon={<TrendingUpIcon fontSize="small" />} 
                  color="#0ea5e9"
                  onClick={() => navigateTo('schedules', 'active-schedules')}
                  description={data?.metricDescriptions?.schedulerAdoption}
                />
              </>
            )}
          </Box>

          {/* Active Schedules Table */}
          <Box id="active-schedules" sx={{ scrollMarginTop: 20 }}>
            <DataGrid
              title="Active Schedules"
              loading={loading}
              columns={[
                { key: 'cronName', label: 'Schedule Name', width: 280, render: (r) => (
                  <Tooltip title={r.cronName as string} arrow>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {r.cronName as string}
                    </Typography>
                  </Tooltip>
                )},
                { key: 'sourceName', label: 'Source', width: 150, render: (r) => (
                  <Tooltip title={`Region: ${r.sourceRegion}`} arrow placement="top">
                    <Chip size="small" label={r.sourceName as string} variant="outlined" />
                  </Tooltip>
                )},
                { key: 'destinationName', label: 'Destination', width: 150, render: (r) => (
                  <Tooltip title={`Region: ${r.destinationRegion}`} arrow placement="top">
                    <Chip size="small" label={r.destinationName as string} variant="outlined" />
                  </Tooltip>
                )},
                { key: 'cron', label: 'Cron', width: 130, render: (r) => (
                  <Chip size="small" label={r.cron as string} sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                )},
                { key: 'lastRun', label: 'Last Run', width: 180, render: (r) => {
                  const lastRun = r.lastRun as string;
                  if (lastRun === 'Never') return <Chip size="small" label="Never" color="warning" variant="outlined" />;
                  return new Date(lastRun).toLocaleString();
                }},
                { key: 'dateCreated', label: 'Created', width: 120, render: (r) => new Date(r.dateCreated as string).toLocaleDateString() },
              ]}
              data={(filteredMetrics?.activeSchedules.schedules ?? []) as unknown as Record<string, unknown>[]}
              emptyMessage="No active schedules"
              emptyIcon={<ScheduleIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />}
              getRowId={(row: Record<string, unknown>) => row.cronName as string}
            />
          </Box>

          {/* Stale Schedules Table */}
          <Box id="stale-schedules" sx={{ scrollMarginTop: 20 }}>
            <DataGrid
              title="Stale Schedules"
              loading={loading}
              columns={[
                { key: 'cronName', label: 'Schedule Name', width: 350, render: (r) => (
                  <Tooltip title={r.cronName as string} arrow>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {r.cronName as string}
                    </Typography>
                  </Tooltip>
                )},
                { key: 'sourceName', label: 'Source', width: 150, render: (r) => (
                  <Tooltip title={`Region: ${r.sourceRegion}`} arrow placement="top">
                    <span>{r.sourceName as string}</span>
                  </Tooltip>
                )},
                { key: 'destinationName', label: 'Destination', width: 150, render: (r) => (
                  <Tooltip title={`Region: ${r.destinationRegion}`} arrow placement="top">
                    <span>{r.destinationName as string}</span>
                  </Tooltip>
                )},
                { key: 'cron', label: 'Cron', width: 130, render: (r) => (
                  <Chip size="small" label={r.cron as string} sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} color="default" />
                )},
                { key: 'lastRun', label: 'Last Run', width: 150, render: (r) => {
                  const lastRun = r.lastRun as string;
                  if (lastRun === 'Never') return <Chip size="small" label="Never run" color="error" variant="outlined" icon={<WarningIcon sx={{ fontSize: 14 }} />} />;
                  return (
                    <Tooltip title={`Last run: ${new Date(lastRun).toLocaleString()}`} arrow>
                      <Chip size="small" label={`${Math.floor((Date.now() - new Date(lastRun).getTime()) / (1000 * 60 * 60 * 24))} days ago`} color="warning" variant="outlined" />
                    </Tooltip>
                  );
                }},
                { key: 'dateCreated', label: 'Created', width: 120, render: (r) => new Date(r.dateCreated as string).toLocaleDateString() },
              ]}
              data={(filteredMetrics?.staleSchedules.schedules ?? []) as unknown as Record<string, unknown>[]}
              emptyMessage="No stale schedules - all schedules are active!"
              emptyIcon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />}
              getRowId={(row: Record<string, unknown>) => row.cronName as string}
            />
          </Box>
        </Stack>
      )}
    </Box>
  );
}
